/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useMemo, useState, useCallback } from 'react'
import { t } from '@sportninja/common/i18n'
import { getTeamOfficialTypesBySportId } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { Table, Typography, Avatar } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'
import Icon from '../../components/Icon'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'

import { SendInvitationTrigger } from '../SendInvitationModal'
import OptionButtonDelete from './OptionButtons/Delete'
import TeamRosterBasedList from './TeamRosterBasedList'
import MoreOptions from './OptionButtons/More'
import { formatPhoneNumber } from 'react-phone-number-input'
import TeamOfficialModal from '../TeamOfficialModal'

const TeamOfficialListTable = ({
  teamId,
  teamName,
  permission,
  teamOfficialTypes,
  refreshKey,
  onComplete,
  sportId,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])

  const [sort, setSort] = useState('name_last')
  const [direction, setDirection] = useState('asc')

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOfficial, setSelectedOfficial] = useState(null)

  const handleEditClick = useCallback((item) => {
    setSelectedOfficial(item)
    setIsModalOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    setSelectedOfficial(null)
  }, [])

  const handleSort = (field, order) => {
    setSort(field)
    setDirection(order)
  }

  const columns = useMemo(() => {
    const cols = [
      {
        title: t('common:name'),
        key: 'name',
        sorter: true,
        width: isMobile ? 200 : 300,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'name_first' || sort === 'name_last'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => {
          return (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                max-width: 268px;
                overflow: hidden;
                color: ${colors.NEUTRAL_0};
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item) ||
                  '/images/generic_placeholders/placeholder_player.png'
                }
                icon={<Icon name='user' fontSize={14} />}
                shape='round'
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  padding: 2px;
                  flex-shrink: 0;
                  width: 32px;
                  height: 32px;
                `}
              />
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Typography.Text
                  strong
                  ellipsis
                  css={css`
                    display: block;
                    width: 100%;
                    color: inherit;
                  `}
                >
                  {item?.name_first} {item?.name_last}
                </Typography.Text>
                {/* {item?.email && (
                  <Typography.Text
                    type='secondary'
                    ellipsis
                    css={css`
                      font-size: 12px;
                    `}
                  >
                    {item.email}
                  </Typography.Text>
                )} */}
              </div>
            </div>
          )
        },
      },
      {
        title: t('common:role'),
        key: 'role',
        sorter: true,
        showSorterTooltip: false,
        width: 150,
        sortOrder:
          sort === 'official_type'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_, item) => {
          return (
            teamOfficialTypes.find((t) => t.id === item.team_official_type)
              ?.name_full || '-'
          )
        },
      },
      {
        title: t('common:email'),
        key: 'email',
        dataIndex: 'email',
        render: (email) => email || '',
      },
      {
        title: t('common:phone'),
        key: 'phone_number',
        dataIndex: 'phone_number',
        render: (phoneNumber) => formatPhoneNumber(phoneNumber) || '',
      },
      {
        title: 'Invite Status',
        key: 'invite_status',
        width: 180,
        render: (_, item) => {
          if (item?.is_accepted) {
            return 'Accepted'
          }
          if (item?.is_invited) {
            return 'Invited'
          }
          return ''
        },
      },
    ]

    if (permission?.update || permission?.admin) {
      cols.push({
        // title: 'Options',
        title: '',
        key: 'options',
        align: 'right',
        width: 150,
        fixed: 'right',
        render: (_, item) => {
          // Placeholder, actual render will be handled in the component body
          return null
        },
      })
    }

    return cols
  }, [isMobile, sort, direction, teamOfficialTypes, permission])

  return (
    <TeamRosterBasedList
      teamId={teamId}
      teamName={teamName}
      permission={permission}
      slug='team-officials'
      refreshKey={refreshKey}
      onComplete={onComplete}
    >
      {(roster, refresh) => {
        const handleModalSuccess = () => {
          refresh()
        }

        const getOptions = (item) => {
          const slug = `/teams/${teamId}/rosters/${roster.id}/team-officials/${item.id}`

          const opts = [
            <button
              key='edit'
              type='button'
              onClick={() => handleEditClick(item)}
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                color: ${colors.PRIMARY};
                padding: 4px 8px;
                font-size: 18px;
                display: flex;
                align-items: center;
                justify-content: center;
                &:hover {
                  opacity: 0.8;
                }
              `}
              title={t('Web:editTeamOfficial')}
            >
              <EditOutlined
                css={css`
                  font-size: 20px;
                `}
              />
            </button>,
            <SendInvitationTrigger
              key='invite'
              invite={{
                is_invited: item.is_invited,
                is_accepted: item.is_accepted,
                invitation_id: item.invitation_id,
              }}
              user={{
                id: item.id,
                name_first: item.name_first,
                name_last: item.name_last,
                team_official_type_id: item.team_official_type,
                email: item?.email,
              }}
              slug={slug}
              onComplete={refresh}
            />,
            <MoreOptions
              key='more'
              options={[
                {
                  key: 'remove',
                  Component: OptionButtonDelete,
                  componentProps: {
                    buttonText: 'remove',
                    slug,
                    iconName: 'user-minus',
                    title: `Remove ${item.name_full}`,
                    type: t('common:teamOfficial'),
                    itemId: item.id,
                    onComplete: refresh,
                  },
                  title: 'remove',
                  iconName: 'user-minus',
                },
              ]}
            />,
          ]

          return opts.filter((o) => o !== undefined)
        }

        const finalColumns = columns.map((col) => {
          if (col.key === 'options') {
            return {
              ...col,
              render: (_, item) => (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    justify-content: flex-end;
                  `}
                >
                  {getOptions(item)}
                </div>
              ),
            }
          }
          return col
        })

        const sorter = (a, b) => {
          if (sort === 'name_last' || sort === 'name_first') {
            // Default to name sorting logic, prioritizing last name then first name or vice versa if needed
            // The original list had 'name_last' as default.
            // Let's implement a simple name sort.
            const nameA = `${a.name_last} ${a.name_first}`.toLowerCase()
            const nameB = `${b.name_last} ${b.name_first}`.toLowerCase()
            if (nameA < nameB) {
              return direction === 'asc' ? -1 : 1
            }
            if (nameA > nameB) {
              return direction === 'asc' ? 1 : -1
            }
            return 0
          }

          if (sort === 'official_type') {
            if (a.team_official_type < b.team_official_type) {
              return direction === 'asc' ? -1 : 1
            }
            if (a.team_official_type > b.team_official_type) {
              return direction === 'asc' ? 1 : -1
            }
            return 0
          }

          return 0
        }

        const sortedItems = [...(roster?.team_officials || [])].sort(sorter)

        return (
          <>
            <div
              css={css`
                width: 100%;
              `}
            >
              <Table
                columns={finalColumns}
                dataSource={sortedItems}
                rowKey='id'
                pagination={false}
                size='small'
                scroll={{ x: 'max-content' }}
                css={getTableStyles(isMobile)}
                onChange={(pagination, filters, tableSorter) => {
                  if (tableSorter.order) {
                    const sortKey = tableSorter.columnKey || tableSorter.field
                    const field =
                      sortKey === 'name'
                        ? 'name_last'
                        : sortKey === 'role'
                        ? 'official_type'
                        : sortKey
                    handleSort(
                      field,
                      tableSorter.order === 'ascend' ? 'asc' : 'desc'
                    )
                  } else {
                    handleSort('name_last', 'asc')
                  }
                }}
              />
            </div>
            <TeamOfficialModal
              isOpen={isModalOpen}
              onClose={handleModalClose}
              initialData={selectedOfficial}
              teamId={teamId}
              rosterId={roster.id}
              onSuccess={handleModalSuccess}
              sportId={sportId}
            />
          </>
        )
      }}
    </TeamRosterBasedList>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    teamOfficialTypes: getTeamOfficialTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(TeamOfficialListTable)
